/**
 * EmailDraftCard Component
 * 
 * Displays individual email draft with job/company info.
 */

import { useState } from 'react';
import { Mail, Briefcase, Building2, Calendar, Eye, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDistanceToNow } from 'date-fns';
import type { OutreachEmail, JobListing, CompanyContact } from '@/types/job-hunt';
import { EmailDetailDialog } from './EmailDetailDialog';

interface EmailDraftCardProps {
  email: OutreachEmail;
  jobListing?: JobListing;
  contact?: CompanyContact;
  onDelete?: (id: string) => void;
}

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
  draft: { label: 'Draft', variant: 'secondary' },
  scheduled: { label: 'Scheduled', variant: 'default' },
  sent: { label: 'Sent', variant: 'outline' },
};

export function EmailDraftCard({ email, jobListing, contact, onDelete }: EmailDraftCardProps) {
  const [showDetail, setShowDetail] = useState(false);
  const config = statusConfig[email.status] || statusConfig.draft;

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <CardTitle className="text-title font-semantic-semibold truncate">
                  {email.subject}
                </CardTitle>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant={config.variant}>{config.label}</Badge>
                <Badge variant="outline" className="gap-1">
                  <span className="text-fine">{email.purpose}</span>
                </Badge>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDetail(true)}
              >
                <Eye className="h-4 w-4" />
              </Button>
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(email.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {/* Body Preview */}
          <p className="text-body text-muted-foreground line-clamp-2">
            {email.body}
          </p>

          {/* Associated Job/Company */}
          <div className="space-y-2">
            {jobListing && (
              <div className="flex items-center gap-2 text-fine">
                <Briefcase className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Job:</span>
                <span className="font-medium">{jobListing.title}</span>
                <span className="text-muted-foreground">at</span>
                <span className="font-medium">{jobListing.company_name}</span>
              </div>
            )}
            {contact && (
              <div className="flex items-center gap-2 text-fine">
                <Building2 className="h-3 w-3 text-muted-foreground" />
                <span className="text-muted-foreground">Contact:</span>
                <span className="font-medium">{contact.contact_name}</span>
                <span className="text-muted-foreground">at</span>
                <span className="font-medium">{contact.company_name}</span>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="flex items-center gap-4 text-fine text-muted-foreground pt-2 border-t">
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>Created {formatDistanceToNow(new Date(email.created_at), { addSuffix: true })}</span>
            </div>
            {email.scheduled_date && (
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>Scheduled for {new Date(email.scheduled_date).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <EmailDetailDialog
        open={showDetail}
        onOpenChange={setShowDetail}
        email={email}
        jobListing={jobListing}
        contact={contact}
      />
    </>
  );
}
